// bottom-up slide-in panel chrome for all engagement-detail drawers
// (finding / evidence / attack / attack-group / subnet / host)
//
// why a shell, not a fully composed component: each drawer branch in
// NodeDetailDrawer already renders its own sticky header (close button
// + body sections). a minimal shell keeps that content unchanged while
// swapping the narrow right-drawer for a wide bottom sheet that fits
// SNMP interface / route / ARP tables without horizontal clip.
//
// animation: translateY(100%) → 0 over 200ms ease-out. mount with
// `entered=false`, flip on next paint so React commits the closed
// state before the transition runs. on close flip back, then unmount
// after the transition so the panel slides out cleanly.
//
// backdrop click + Escape both call onClose. close button lives in
// each branch's own sticky header, shell doesn't render one

import React, { useEffect, useRef, useState } from 'react'

interface BottomSheetShellProps {
  open:        boolean
  onClose:     () => void
  children:    React.ReactNode
  ariaLabel?:  string
  /** max-height as a vh number (defaults to 86) */
  maxVh?:      number
}

const TRANSITION_MS = 200

export const BottomSheetShell: React.FC<BottomSheetShellProps> = ({
  open, onClose, children, ariaLabel, maxVh = 86,
}) => {
  const [mounted, setMounted] = useState(open)
  const [entered, setEntered] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null }
    if (open) {
      setMounted(true)
      // requestAnimationFrame guarantees the initial translateY(100%)
      // commits before we toggle to translateY(0). otherwise React
      // batches both commits and the transition gets skipped
      const id = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(id)
    } else {
      setEntered(false)
      timerRef.current = setTimeout(() => setMounted(false), TRANSITION_MS)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!mounted) return null

  return (
    <div className="fixed inset-0 z-50" aria-modal="true" role="dialog" aria-label={ariaLabel}>
      {/* light tint so the page stays visible behind the sheet */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-slate-900/25 transition-opacity duration-200 ${entered ? 'opacity-100' : 'opacity-0'}`}
      />
      {/* sheet, pinned to bottom, slides up. shell provides the outer
          chrome only. each branch in NodeDetailDrawer renders its own
          sticky header + close button inside */}
      <div
        className={`absolute inset-x-0 bottom-0 bg-white border-t border-slate-200 rounded-t-xl shadow-2xl transition-transform duration-200 ease-out flex flex-col ${entered ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ maxHeight: `${maxVh}vh` }}
        onClick={e => e.stopPropagation()}
      >
        {/* drag-handle accent, non-interactive, signals dismissable */}
        <div className="flex justify-center pt-2 flex-shrink-0">
          <div className="w-10 h-1 rounded-full bg-slate-200" />
        </div>
        {/* body, scrolls inside maxVh. children supply their own
            sticky header (per drawer branch) so close UI is local */}
        <div className="overflow-y-auto flex-1">
          <div className="mx-auto max-w-[1200px] w-full">
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
